import { FieldStructureMap } from '../../../service-request-form-response.mapper.util';
import { GroupedChecklistInsertDefinition } from '../shared/static-form-section.util';
import { buildChecklistField } from '../shared/static-form-fields.util';

const field = (
  controlKey: string,
  label: string,
  sequence: number,
  overrides: Partial<FieldStructureMap[string]> = {},
): [string, FieldStructureMap[string]] => [
  controlKey,
  {
    type: overrides.type ?? 'TEXT',
    label,
    sequence,
    required: overrides.required ?? false,
    ...overrides,
  },
];

export const VENTI_CHECKLIST_ROW_PHOTO_MAX_COUNT = 3;
export const VENTI_NAMEPLATE_PHOTO_MAX_COUNT = 1;
export const VENTI_EXTERNAL_FOOTER_PHOTO_MAX_COUNT = 1;

export const VENTI_VESSEL_U_STAMP_KEY = 'vesselUStamp';

export const VENTI_VESSEL_HEAD_TYPE_OPTIONS = [
  { label: 'Semi-Elliptical', value: 'Semi-Elliptical' },
  { label: 'Hemi-spherical', value: 'Hemi-spherical' },
] as const;

export const VENTI_VESSEL_BASE_TYPE_OPTIONS = [
  { label: 'Skip', value: 'Skip' },
] as const;

export const VENTI_VESSEL_TOTAL_SCFM_OPTIONS = [
  { label: '13,540', value: '13,540' },
] as const;

export const VENTI_PRESSURE_VESSEL_LEFT_ENTRIES: [string, { type?: 'select' | 'textarea'; required?: boolean; maxLength?: number; readOnly?: boolean }][] = [
  ['vesselInspector', { required: true }],
  ['vesselSerialNumber', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselCapacityUswg', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselServiceTypeGrid', { readOnly: true }],
  ['vesselProvincialNo', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselCrnNo', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselMawp', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselYearBuilt', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselManufacturedBy', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselManwayLocation', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselHeadType', { type: 'select', required: true }],
  ['vesselBaseType', { type: 'select', required: true }],
];

export const VENTI_PRESSURE_VESSEL_RIGHT_ENTRIES: [string, { type?: 'select' | 'textarea'; required?: boolean; maxLength?: number }][] = [
  ['vesselHeadMaterial', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselShellMaterial', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselHeadThickness', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselShellThickness', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselDiameter', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselLength', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselSurfaceAreaSqFt', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselPrvCountAvailable', { type: 'textarea', required: true, maxLength: 100 }],
  ['vesselTotalScfmRequired', { type: 'select', required: true }],
];

export const VENTI_PRV_GRID_LEFT_ENTRIES: [string, { type?: 'select' | 'textarea'; required?: boolean; maxLength?: number }][] = [
  ['ventiPrvManufacturer', { type: 'select', required: true }],
  ['ventiPrvModel', { type: 'textarea', required: true, maxLength: 100 }],
  ['ventiPrvCertificationUl', { type: 'textarea', required: true, maxLength: 100 }],
  ['ventiPrvSpringType', { type: 'textarea', required: true, maxLength: 100 }],
  ['ventiPrvInspectorComments', { type: 'textarea', maxLength: 150 }],
];

export const VENTI_PRV_GRID_RIGHT_ENTRIES: [string, { type?: 'select' | 'textarea'; required?: boolean; maxLength?: number }][] = [
  ['ventiPrvScfmRating', { type: 'select', required: true }],
  ['ventiPrvDate', { type: 'textarea', required: true, maxLength: 100 }],
  ['ventiPrvPressureSetting', { type: 'textarea', required: true, maxLength: 100 }],
];

export const VENTI_PRV_MANUFACTURER_OPTIONS = [
  { label: 'In Progress', value: 'In Progress' },
  { label: 'Fisher', value: 'Fisher' },
  { label: 'RegO', value: 'RegO' },
] as const;

export const VENTI_PRV_SCFM_OPTIONS = [
  { label: '2565', value: '2565' },
  { label: '3000', value: '3000' },
  { label: '3500', value: '3500' },
] as const;

export const VENTI_PRV_VISUAL_CHECKLIST_KEYS = [
  'ventiPrvVisualSafetyValveSeals',
  'ventiPrvVisualDischargePipingVented',
  'ventiPrvVisualDischargePipingAttached',
  'ventiPrvVisualReliefValveRainCaps',
] as const;

const VENTI_PRV_VISUAL_LABELS: Record<string, string> = {
  ventiPrvVisualSafetyValveSeals: 'Safety valve(s) seals intact?',
  ventiPrvVisualDischargePipingVented: 'Discharge Piping vented properly?',
  ventiPrvVisualDischargePipingAttached: 'Discharge piping securely attached?',
  ventiPrvVisualReliefValveRainCaps: 'Relief valve rain caps in place?',
};

export const VENTI_EXTERNAL_VISUAL_LABELS: Record<string, string> = {
  externalVisual01: 'Vessel shel and head external condition',
  externalVisual02: 'Liquid ISC valve installed and labelled',
  externalVisual03: 'ISC controls condition and fusible links',
  externalVisual04: 'Roto gauge condition / functional',
  externalVisual05: 'Float gauge condition / functional',
  externalVisual06: 'Pressure gauge',
  externalVisual07: 'Thermometer operational',
  externalVisual08: 'Vessel perimeter protection & code compliant',
  externalVisual09: 'Vessel instrumentation (The associated instrumentation is in good mechanical condition)',
  externalVisual10: 'Vessel free of leaks (smell or audible)',
  externalVisual11: 'Clearance to combustibles',
  externalVisual12:
    'Vessel flange & NPT connection condition (The flange & NPT connections are in good mechanical condition All associated flanges and bolting are intact and show no evidence of mechanical damage, deterioration, visible cracking or process leaks)',
  externalVisual13: 'Condition of tank legs, piers, saddles and mounting bolts is acceptable',
  externalVisual14: 'Vessel is level',
  externalVisual15: 'Is there any evidence of pier movement',
  externalVisual16: 'External paint condition acceptable',
  externalVisual17:
    'Threaded pipe connections (The threaded piping is in place and tight with no evidence of deterioration, visible cracking, or process leaks.)',
  externalVisual18: 'Flanged connections - Nuts & Studs',
  externalVisual19: 'Piping ground clearance',
  externalVisual20: 'Pipe supports in good condition',
  externalVisual21: 'Piping free of leaks',
  externalVisual22:
    'Valves / piping - external condition. The primary piping is threaded The piping is in good mechanical condition with no signs of damage or deformation.',
  externalVisual23: 'Excess flow & back check valves in place',
  externalVisual24: 'Hydrostatic relief valves/rain caps in place',
  externalVisual25: 'Snappy-joes in place and functioning',
  externalVisual26: 'ACME adaptors / caps condition',
  externalVisual27: 'Bonding cable available',
  externalVisual28: 'Grounding or bonding connections compliant',
  externalVisual29: 'Electrical connections compliant',
  externalVisual30: 'Emergency Shut Down in place and operational',
  externalVisual31: 'Bulkhead includes 24" standpipes or appropriate breakaways',
  externalVisual32: 'External paint condition acceptable',
};

export const VENTI_EXTERNAL_VISUAL_CHECKLIST_KEYS = Object.keys(VENTI_EXTERNAL_VISUAL_LABELS) as (keyof typeof VENTI_EXTERNAL_VISUAL_LABELS)[];

export const VENTI_BULK_STORAGE_LABELS: Record<string, string> = {
  bulkStorage01: 'Maintenance & Operating Procedures available',
  bulkStorage02: 'ERAP available and accessible',
  bulkStorage03: 'Truck parking clearances acceptable',
  bulkStorage04: 'Yard is clean and organized',
  bulkStorage05: 'Vegitation controlled',
  bulkStorage06: 'Fence and gates condition / secure',
  bulkStorage07: 'Grading by piers to provide drainage',
  bulkStorage08: 'Truck loading/unloading areas level',
  bulkStorage09: '1075 signs posted at loading risers',
  bulkStorage10: '20 lb Fire extinguishers and signs',
  bulkStorage11: 'First Aid kit',
  bulkStorage12: 'Guarding on rotating equipment',
  bulkStorage13: 'WHMIS decals present on tank',
  bulkStorage14: 'Hose test completed',
};

export const VENTI_BULK_STORAGE_CHECKLIST_KEYS = Object.keys(VENTI_BULK_STORAGE_LABELS) as (keyof typeof VENTI_BULK_STORAGE_LABELS)[];

export const VENTI_BULK_STORAGE_INSERT_AFTER: GroupedChecklistInsertDefinition[] = [
  {
    afterItemNumber: 10,
    fieldKeys: ['bulkStorageAfter10Comments'],
    fieldOptions: { bulkStorageAfter10Comments: { type: 'date', fullWidth: true, required: true } },
  },
  {
    afterItemNumber: 14,
    fieldKeys: ['bulkStorageAfter14Comments'],
    fieldOptions: { bulkStorageAfter14Comments: { type: 'date', fullWidth: true, required: true } },
  },
];

const pressureVesselFields = (startSequence: number): [string, FieldStructureMap[string]][] => {
  let seq = startSequence;
  const next = (controlKey: string, label: string, overrides: Partial<FieldStructureMap[string]> = {}) => {
    const entry = field(controlKey, label, seq, overrides);
    seq += 1;
    return entry;
  };

  return [
    buildChecklistField(VENTI_VESSEL_U_STAMP_KEY, 'U Stamp', seq++),
    next('vesselServiceTypePrimary', 'Service Type', { readOnly: true }),
    next('vesselInspector', 'Inspector', { type: 'USER_LOOK_UP', required: true }),
    next('vesselSerialNumber', 'Serial Number', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselCapacityUswg', 'Capacity (USWG)', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselServiceTypeGrid', 'Service Type', { readOnly: true }),
    next('vesselProvincialNo', 'Provincial No.', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselCrnNo', 'CRN No.', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselMawp', 'MAWP', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselYearBuilt', 'Year Built', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselManufacturedBy', 'Manufactured By', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselManwayLocation', 'Manway Location', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselHeadType', 'Head Type', {
      type: 'SINGLE',
      required: true,
      subQuestionLabels: ['Semi-Elliptical', 'Hemi-spherical'],
    }),
    next('vesselBaseType', 'Base Type', { type: 'SINGLE', required: true, subQuestionLabels: ['Skip'] }),
    next('vesselHeadMaterial', 'Head Material', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselShellMaterial', 'Shell Material', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselHeadThickness', 'Head Thickness', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselShellThickness', 'Shell Thickness', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselDiameter', 'Diameter', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselLength', 'Length', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselSurfaceAreaSqFt', 'Surface Area in square feet', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselPrvCountAvailable', 'Number of PRVs Available', { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    next('vesselTotalScfmRequired', 'Total SCFM Required', {
      type: 'SINGLE',
      required: true,
      subQuestionLabels: ['13,540'],
    }),
    next('vesselDataComments', 'Comments', { type: 'TEXT_AREA', maxLength: 150 }),
    next('vesselNameplatePhoto', 'Nameplate Photo', {
      type: 'IMAGE',
      maxPhotoCount: VENTI_NAMEPLATE_PHOTO_MAX_COUNT,
    }),
  ];
};

const ventiPrvBaseFields = (startSequence: number): [string, FieldStructureMap[string]][] => {
  let seq = startSequence;
  const entries: [string, FieldStructureMap[string]][] = [
    field('ventiPrvManufacturer', 'PRV Manufacturer', seq++, {
      type: 'SINGLE',
      required: true,
      subQuestionLabels: ['In Progress', 'Fisher', 'RegO'],
    }),
    field('ventiPrvModel', 'PRV Model', seq++, { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    field('ventiPrvCertificationUl', 'Certification - UL', seq++, { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    field('ventiPrvSpringType', 'Spring Type', seq++, { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    field('ventiPrvScfmRating', 'SCFM Rating', seq++, {
      type: 'SINGLE',
      required: true,
      subQuestionLabels: ['2565', '3000', '3500'],
    }),
    field('ventiPrvDate', 'PRV Date - Enter month and year as MM/YY', seq++, {
      type: 'TEXT_AREA',
      required: true,
      maxLength: 100,
    }),
    field('ventiPrvPressureSetting', 'Pressure Setting', seq++, { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    field('ventiPrvInspectorComments', 'Inspector Comments', seq++, { type: 'TEXT_AREA', maxLength: 150 }),
    ...VENTI_PRV_VISUAL_CHECKLIST_KEYS.map((controlKey) =>
      buildChecklistField(controlKey, VENTI_PRV_VISUAL_LABELS[controlKey], seq++),
    ),
  ];
  return entries;
};

const externalVisualFields = (startSequence: number): [string, FieldStructureMap[string]][] => {
  let seq = startSequence;
  const checklist = VENTI_EXTERNAL_VISUAL_CHECKLIST_KEYS.map((controlKey) =>
    buildChecklistField(controlKey, VENTI_EXTERNAL_VISUAL_LABELS[controlKey], seq++),
  );
  return [
    ...checklist,
    field('externalVisualComments', 'Comments', seq++, { type: 'TEXT_AREA', maxLength: 150, isChecklistFooter: true }),
    field('externalVisualFooterPhotos', 'Photos', seq++, {
      type: 'IMAGE',
      maxPhotoCount: VENTI_EXTERNAL_FOOTER_PHOTO_MAX_COUNT,
    }),
  ];
};

const bulkStorageFields = (startSequence: number): [string, FieldStructureMap[string]][] => {
  let seq = startSequence;
  const checklist = VENTI_BULK_STORAGE_CHECKLIST_KEYS.map((controlKey) =>
    buildChecklistField(controlKey, VENTI_BULK_STORAGE_LABELS[controlKey], seq++),
  );
  return [
    ...checklist,
    field('bulkStorageAfter10Comments', 'Fire Extinguisher Inspection Date', seq++, {
      type: 'DATE',
      required: true,
    }),
    field('bulkStorageAfter14Comments', 'Date Completed', seq++, { type: 'DATE', required: true }),
    field('bulkStorageFooterComments', 'Comments', seq++, {
      type: 'TEXT_AREA',
      maxLength: 150,
      isChecklistFooter: true,
    }),
  ];
};

export const buildVentiExtendedSectionFields = (startSequence: number): [string, FieldStructureMap[string]][] => [
  ...pressureVesselFields(startSequence),
  ...ventiPrvBaseFields(startSequence + 30),
  ...externalVisualFields(startSequence + 50),
  ...bulkStorageFields(startSequence + 90),
];

export const VENTI_PRV_INSTANCE_FIELD_KEYS = [
  'ventiPrvManufacturer',
  'ventiPrvModel',
  'ventiPrvCertificationUl',
  'ventiPrvSpringType',
  'ventiPrvScfmRating',
  'ventiPrvDate',
  'ventiPrvPressureSetting',
  'ventiPrvInspectorComments',
  ...VENTI_PRV_VISUAL_CHECKLIST_KEYS,
] as const;
